import { createApi } from '@reduxjs/toolkit/query/react';
import {
  CreateProjectBodyPrivate,
  GetManyProjectsQueryPrivate,
  GetManyProjectsResponsePrivate,
  ProjectResponsePrivate,
  UpdateProjectBodyPrivate,
} from '@tests/types';

import { ApiPath } from '@/constants';
import { createUrl } from '@/utils';

import { authGuardQuery } from './queries';

export const projectsApi = createApi({
  baseQuery: authGuardQuery,
  endpoints: (build) => ({
    createProject: build.mutation<ProjectResponsePrivate, CreateProjectBodyPrivate>({
      invalidatesTags: [{ id: 'List', type: 'Projects' }],
      query: (data) => ({
        data,
        method: 'POST',
        url: createUrl(ApiPath.Projects),
      }),
    }),
    deleteProject: build.mutation<void, number | string>({
      invalidatesTags: [{ id: 'List', type: 'Projects' }],
      query: (id) => ({
        method: 'DELETE',
        url: createUrl(ApiPath.Projects, id),
      }),
    }),
    getProjectById: build.query<ProjectResponsePrivate, RelationsParams & { id: number }>({
      providesTags: (test) => [{ id: test?.id, type: 'Projects' }],
      query: ({ id, ...params }) => ({
        method: 'GET',
        params: {
          ...params,
        },
        url: createUrl(ApiPath.Projects, id),
      }),
    }),
    getProjectsList: build.query<
      GetManyProjectsResponsePrivate,
      RelationsParams & Partial<GetManyProjectsQueryPrivate>
    >({
      providesTags: (result) => [
        ...(result?.results?.map((project) => ({
          id: project.id,
          type: 'Projects' as const,
        })) || []),
        { id: 'List', type: 'Projects' },
      ],
      query: (params) => ({
        method: 'GET',
        params: {
          ...params,
        },
        url: createUrl(ApiPath.Projects),
      }),
    }),
    updateProjectById: build.mutation<
      ProjectResponsePrivate,
      UpdateProjectBodyPrivate & { id: number }
    >({
      invalidatesTags: (result) => [{ id: result?.id, type: 'Projects' }],
      query: ({ id, ...data }) => ({
        data,
        method: 'PATCH',
        url: createUrl(ApiPath.Projects, id),
      }),
    }),
  }),
  reducerPath: 'projectsApi',
  tagTypes: ['Projects'],
});

export const {
  useCreateProjectMutation,
  useDeleteProjectMutation,
  useGetProjectByIdQuery,
  useGetProjectsListQuery,
  useUpdateProjectByIdMutation,
} = projectsApi;
